/**
 * Заявки на прогон мониторинга по кнопке «Запустить генерацию» (docs/operations.md,
 * раздел «Прогон генерации: кнопка, очередь, демон»).
 *
 * Кнопка обязана запускать ВЕСЬ конвейер: мониторинг → темы → отбор → генерация → бэклог,
 * а не только генерацию по уже найденным темам. Регулярный прогон по расписанию честно
 * может не создать ни одной темы (ПРАВИЛО 6.22, 01-monitoring §17.3); ручной — обязан
 * вернуть хотя бы одну, иначе кнопка бесполезна в день, когда обход ничего не нашёл.
 * Поэтому у ручного прогона своя очередь: бэкенд заводит заявку на мониторинг вместе с
 * заявкой на генерацию, мониторинг забирает её (`POST /intake/v1/monitoring-requests/claim`,
 * скоуп `runs:write`), гонит ослабленный отбор и отчитывается
 * (`POST /intake/v1/monitoring-requests/:id/finish`); сервис генерации читает состояние
 * по своей заявке (`GET /intake/v1/generation-requests/:id/monitoring`, скоуп `drafts:read`)
 * и ждёт, прежде чем брать тему.
 *
 * Как мониторинг узнаёт о заявке: бэкенд кладёт триггер-файл в свой state-каталог, а
 * systemd path-юнит (`smm-monitor-manual.path`) поднимает `smm-monitor-manual.service` —
 * тот же код мониторинга с флагом `--manual`. Файл — только сигнал «очередь непуста»,
 * сама очередь живёт в базе; ни одна сторона не читает чужих файлов.
 *
 * Взятие заявки НЕ идемпотентно по ключу (маршрут объявлен `noIdempotency`), но
 * идемпотентно по `worker_id`: бэкенд сначала отдаёт заявку, уже взятую этим же
 * воркером и не закрытую, и только потом снимает следующую из очереди. Повтор после
 * оборванного ответа возвращает ту же заявку, а не съедает вторую.
 */

import * as z from 'zod';
import { BRAND_RE } from '../approval-key/limits.ts';
import { UUID_V7_RE } from '../ids.ts';
import { ISO_UTC_MS } from '../time.ts';

const Uuid = z.string().regex(UUID_V7_RE);
const IsoUtcMs = z.string().regex(ISO_UTC_MS);

/**
 * Жизненный цикл: `queued` → `taken` двигает мониторинг взятием, `taken` → `done|failed` —
 * он же отчётом; `queued|taken` → `failed` — уборка бэкенда по сроку (заявка, которую никто
 * не взял или не закрыл). Отмены нет: заявку на мониторинг никто не нажимал отдельно.
 */
export const MONITORING_REQUEST_STATES = ['queued', 'taken', 'done', 'failed'] as const;
export const MonitoringRequestStateSchema = z.enum(MONITORING_REQUEST_STATES);
export type MonitoringRequestState = z.output<typeof MonitoringRequestStateSchema>;

/**
 * Вид заявки (docs/release-2026-09-19.md §4). `topic` — прежний ручной прогон: найти тему
 * для писателя (или прочитать статью по ссылке). `scan` — «только мониторинг»: обойти
 * источники и оценить материалы, НЕ заводя ни одной темы. Тема здесь — не мелочь: любую
 * `proposed` тему сметающий проход писателя берёт в работу сам, и скан с темами через
 * четверть часа оказался бы прогоном с писателем.
 */
export const MONITORING_REQUEST_MODES = ['topic', 'scan'] as const;
export const MonitoringRequestModeSchema = z.enum(MONITORING_REQUEST_MODES);
export type MonitoringRequestMode = z.output<typeof MonitoringRequestModeSchema>;

/** Потолок списка тем в отчёте: ручной прогон и так создаёт единицы, не десятки. */
export const MONITORING_REQUEST_TOPICS_MAX = 20;
/** Потолок текста исхода — как у `outcome_detail` заявки на генерацию: тот же жанр. */
export const MONITORING_REQUEST_DETAIL_MAX = 2000;

// ─────────────────────────────────────────────────────────────────────────────
// POST /intake/v1/monitoring-requests/claim — мониторинг берёт заявку

export const MonitoringRequestClaimBodySchema = z.strictObject({
  /** host/pid — тот же формат, что у `worker_id` прогона (api/runs.ts). */
  worker_id: z.string().min(1).max(128),
  /**
   * Сервис читает `deadline_at` задания (P3). Поле — согласие на новую форму ответа: прежний
   * мониторинг его не шлёт и получает задание прежней строгой формы, без `deadline_at`.
   */
  with_deadline: z.literal(true).optional(),
});
export type MonitoringRequestClaimBody = z.output<typeof MonitoringRequestClaimBodySchema>;

/**
 * Заявка в том виде, в каком её читает мониторинг. Подсказки человека (формат, промпт,
 * ссылка) едут копией из заявки на генерацию: мониторинг подбирает материал под них,
 * а сервис генерации потом применит их же к тексту.
 */
export const MonitoringRequestTaskSchema = z.strictObject({
  request_id: Uuid,
  brand: z.string().regex(BRAND_RE),
  /** Заявка на генерацию, ради которой заведён прогон; null — заведена не кнопкой. */
  generation_request_id: Uuid.nullable(),
  mode: MonitoringRequestModeSchema,
  format_hint: z.string().nullable(),
  prompt: z.string().nullable(),
  source_url: z.string().nullable(),
  /**
   * Статью выбрал ЧЕЛОВЕК в таблице прогона (§3.4). Непусто ⟹ `source_url` — её адрес, и
   * мониторинг читает её как ссылку редактора, но вердикт модели «не брать» и дедуп темы
   * прогон НЕ останавливают: что читать, уже решил человек, а модель даёт только угол.
   */
  pick_material_id: Uuid.nullable(),
  created_at: IsoUtcMs,
  taken_at: IsoUtcMs,
  /**
   * Единый срок прогона (P3, находка 19): к этому моменту сервис генерации, ждущий заявку,
   * сдаётся. Ожидание пересечения, обход, вызовы модели и заведение темы обязаны уложиться в
   * него; `null` — ждущего нет (расписание, скан). Только при `with_deadline` во взятии.
   */
  deadline_at: IsoUtcMs.nullable().optional(),
});
export type MonitoringRequestTask = z.output<typeof MonitoringRequestTaskSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// POST /intake/v1/monitoring-requests/release — перезапущенный мониторинг отпускает своё

/**
 * Процесс мониторинга на старте отпускает заявки, взятые его предшественником под тем же
 * устойчивым `worker_id` (P3, находка 41): прогон предшественника закрывается и освобождает
 * замок бренда, заявка возвращается в очередь (попытки и срок позволяют) или закрывается
 * `failed` с причиной — вместо 25 минут сироты и 15 минут чужого замка.
 */
export const MonitoringRequestReleaseBodySchema = z.strictObject({
  worker_id: z.string().min(1).max(128),
});
export type MonitoringRequestReleaseBody = z.output<typeof MonitoringRequestReleaseBodySchema>;

export const MonitoringRequestReleasedSchema = z.strictObject({
  released: z.int().min(0),
  requeued: z.int().min(0),
});
export type MonitoringRequestReleased = z.output<typeof MonitoringRequestReleasedSchema>;

/** `request: null` — очередь пуста: штатный ответ, а не отказ. */
export const MonitoringRequestClaimedSchema = z.strictObject({
  request: MonitoringRequestTaskSchema.nullable(),
});
export type MonitoringRequestClaimed = z.output<typeof MonitoringRequestClaimedSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// POST /intake/v1/monitoring-requests/:id/finish — мониторинг отчитывается

export const MONITORING_REQUEST_OUTCOMES = ['done', 'failed'] as const;
export const MonitoringRequestOutcomeSchema = z.enum(MONITORING_REQUEST_OUTCOMES);
export type MonitoringRequestOutcome = z.output<typeof MonitoringRequestOutcomeSchema>;

/**
 * У заявки вида `topic` `done` означает «хотя бы одна тема, которую можно брать в работу»:
 * новая или уже существовавшая `proposed` по тому же материалу (409 duplicate_topic отдаёт
 * её id). У скана `done` означает «обход прошёл», и тем в нём нет по определению.
 *
 * ПОЭТОМУ ПРАВИЛО «done без тем — противоречие» ПЕРЕЕХАЛО ИЗ СХЕМЫ В БЭКЕНД (0019): вид
 * заявки знает бэкенд, а тело отчёта его не несёт — поле, которое сервис мог бы прислать
 * не тем, было бы полем, которому можно соврать. Бэкенд отвечает 422
 * `done_requires_topic_ids` ровно там, где раньше отвечала схема, и CHECK таблицы держит
 * то же правило третьей линией. `topics_created` считает только новые строки, оттого он
 * не больше длины списка — это правило формы и осталось здесь.
 */
export const MonitoringRequestFinishBodySchema = z
  .strictObject({
    outcome: MonitoringRequestOutcomeSchema,
    /** Прогон, в котором заявка обслужена; null — прогон открыть не удалось. */
    run_id: Uuid.nullable(),
    topics_created: z.int().min(0),
    topic_ids: z.array(Uuid).max(MONITORING_REQUEST_TOPICS_MAX),
    detail: z.string().min(1).max(MONITORING_REQUEST_DETAIL_MAX).nullable(),
  })
  .check((ctx) => {
    const v = ctx.value;
    if (v.topics_created > v.topic_ids.length) {
      ctx.issues.push({
        code: 'custom',
        input: v,
        path: ['topics_created'],
        message: 'topics_created_exceeds_topic_ids',
      });
    }
  });
export type MonitoringRequestFinishBody = z.output<typeof MonitoringRequestFinishBodySchema>;

// ─────────────────────────────────────────────────────────────────────────────
// GET /intake/v1/generation-requests/:id/monitoring — сервис генерации ждёт мониторинг

/**
 * Состояние прогона мониторинга глазами заявки на генерацию. `topic_ids` — то, что
 * сервису генерации брать в первую очередь: тема, найденная ради этой кнопки, а не
 * любая `proposed` из очереди.
 */
export const GenerationMonitoringSchema = z.strictObject({
  request_id: Uuid,
  state: MonitoringRequestStateSchema,
  run_id: Uuid.nullable(),
  topics_created: z.int().min(0),
  topic_ids: z.array(Uuid).max(MONITORING_REQUEST_TOPICS_MAX),
  outcome_detail: z.string().nullable(),
  created_at: IsoUtcMs,
  taken_at: IsoUtcMs.nullable(),
  finished_at: IsoUtcMs.nullable(),
});
export type GenerationMonitoring = z.output<typeof GenerationMonitoringSchema>;

/** `monitoring: null` — у заявки нет прогона мониторинга (заведена до этой возможности). */
export const GenerationMonitoringResponseSchema = z.strictObject({
  monitoring: GenerationMonitoringSchema.nullable(),
});
export type GenerationMonitoringResponse = z.output<typeof GenerationMonitoringResponseSchema>;
